import { TreePine, Shield, Box, Ruler, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PlatformProduct, UnitCode } from '~/graphql/graphql';
import { getPropertyStringSafe } from '~/utils/properties.tsx';
import { MarketPricePill } from '~/components/ui/MarketPricePill';
import { PiecesPerBundleLabel } from '~/components/ui/PiecesPerBundleLabel';
import { VehicleCapacityEditor } from '~/components/ui/VehicleCapacityEditor';
import type { TransportMode } from '~/hooks/useTransportState';
import { useSubmitVehicleCapacity } from '~/hooks/useSubmitVehicleCapacity';
import { getVehiclePayload, type PlatformProductsLength } from '~/utils/order';

type ProductDetailsProps = {
  listingCount: number;
  sellersCount: number;
  platformProduct: PlatformProduct;
  marketPrice: string | null;
  selectedLength: PlatformProductsLength | null;
  capacityUnit: UnitCode;
  vehicleLabel: TransportMode;
  transportVehicleId: string | null;
};

export function ProductDetails({
  listingCount,
  sellersCount,
  platformProduct,
  marketPrice,
  selectedLength,
  capacityUnit,
  vehicleLabel,
  transportVehicleId,
}: Readonly<ProductDetailsProps>) {
  const { t } = useTranslation('order-builder');
  const submitVehicleCapacity = useSubmitVehicleCapacity();
  return (
    <div
      data-testid="product-details"
      className="bg-neut-light rounded-lg border border-neut-border p-4 sm:p-6 shadow-neut-100 "
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h1 className="text-2xl text-neut-prim font-semibold">
              {platformProduct.name}
            </h1>
            {marketPrice && <MarketPricePill price={marketPrice} />}
          </div>
          <p className="text-sm text-neut-sec">
            {t('order_builder.product_code')}: {platformProduct.code}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <PiecesPerBundleLabel
              piecesPerBundle={platformProduct.piecesPerBundle.value}
              linePieces={0}
            />
            {selectedLength && (
              <VehicleCapacityEditor
                platformProductsLength={selectedLength}
                quantityUnit={capacityUnit}
                vehiclePayload={
                  transportVehicleId
                    ? getVehiclePayload(
                        selectedLength.platformProductVehicleCapacities,
                        transportVehicleId
                      )
                    : null
                }
                vehicleLabel={vehicleLabel}
                onCapacitySubmit={(value) => {
                  if (transportVehicleId) {
                    submitVehicleCapacity({
                      platformProductLengthId: selectedLength.id,
                      transportVehicleId,
                      value,
                      unit: capacityUnit,
                    });
                  }
                }}
                disabled={!transportVehicleId}
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-8">
          <div className="text-right">
            <p className="text-sm text-neut-sec mb-1">
              {t('order_builder.available_from')}
            </p>
            <p className="text-2xl text-prim font-semibold">
              {sellersCount} {t('order_builder.sellers')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-neut-sec mb-1">
              {t('order_builder.total_listings')}
            </p>
            <p className="text-2xl text-prim font-semibold">{listingCount}</p>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 pt-6 border-t border-neut-border min-w-[48rem]">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TreePine
                className="w-3.5 h-3.5 text-neut-400"
                aria-hidden="true"
              />
              <p className="text-xs text-neut-500 uppercase">
                {t('order_builder.details_table.species')}
              </p>
            </div>
            <p className="text-sm font-semibold text-neut-900">
              {getPropertyStringSafe(platformProduct.properties, 'species')}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield
                className="w-3.5 h-3.5 text-neut-400"
                aria-hidden="true"
              />
              <p className="text-xs text-neut-500 uppercase">
                {t('order_builder.details_table.grade')}
              </p>
            </div>
            <p className="text-sm font-semibold text-neut-900">
              #{getPropertyStringSafe(platformProduct.properties, 'grade')}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Box className="w-3.5 h-3.5 text-neut-400" aria-hidden="true" />
              <p className="text-xs text-neut-500 uppercase">
                {t('order_builder.details_table.category')}
              </p>
            </div>
            <p className="text-sm font-semibold text-neut-900">
              {platformProduct.category}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Ruler className="w-3.5 h-3.5 text-neut-400" aria-hidden="true" />
              <p className="text-xs text-neut-500 uppercase">
                {t('order_builder.details_table.thickness')}
              </p>
            </div>
            <p className="text-sm font-semibold text-neut-900">
              {platformProduct.actualThicknessInches}"
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Ruler className="w-3.5 h-3.5 text-neut-400" aria-hidden="true" />
              <p className="text-xs text-neut-500 uppercase">
                {t('order_builder.details_table.width')}
              </p>
            </div>
            <p className="text-sm font-semibold text-neut-900">
              {platformProduct.actualWidthInches}"
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Package
                className="w-3.5 h-3.5 text-neut-400"
                aria-hidden="true"
              />
              <p className="text-xs text-neut-500 uppercase">
                {t('order_builder.details_table.finish')}
              </p>
            </div>
            <p className="text-sm font-semibold text-neut-900">
              {platformProduct.finishCode}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
